import * as React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { transparentize } from 'polished';

import {
  TableCellAlign,
  TableContext,
  TableDensity,
  TableSortDirection,
} from './Table';
import { baseTableCellStyle, buildCellPaddingStyle } from './TableCell';
import { getAriaSort, getAriaSortLabel, getTableSortIcon } from './utils';
import { I18nContext } from '../..';
import { ThemeContext } from '../../theme/ThemeContext';

export interface TableHeaderCellProps
  extends React.HTMLAttributes<HTMLTableCellElement> {
  /**
   * Text alignment of the cell content. Right alignment should be used for numeric values
   * @default TableCellAlign.left
   */
  align?: TableCellAlign;
  /**
   * @default false
   * If true, style as a row header rather than a column header
   */
  isRowHeader?: boolean;
  /**
   * If true, the header will render a button for sorting
   */
  isSortable?: boolean;
  /**
   * Event that fires when clicking the table header cell sort button
   */
  onSort?: () => void;
  /**
   * Direction and range of data cells that are covered by the header cell
   * @default TableHeaderCellScope.col
   */
  scope?: TableHeaderCellScope;
  /**
   * Direction by which the column is sorted
   * @default TableSortDirection.none
   */
  sortDirection?: TableSortDirection;
  /**
   * @internal
   */
  testId?: string;
  /**
   * Width of the component, set by CSS
   * @default auto
   */
  width?: string | number;
  /**
   * Indicates how many columns the header cell spans or extends
   */
  colSpan?: number;
  /**
   * Indicates how many rows the header cell spans or extends
   */
  rowSpan?: number;
  /**
   * @internal
   */
  header?: string;
}

export enum TableHeaderCellScope {
  col = 'col',
  colgroup = 'colgroup',
  row = 'row',
  rowgroup = 'rowgroup',
}

const StyledTableHeaderCell = styled.th<{
  density?: TableDensity;
  hasSquareCorners?: boolean;
  hasVerticalBorders?: boolean;
  isInverse?: boolean;
  isRowHeader?: boolean;
  isSortable?: boolean;
  textAlign?: TableCellAlign;
  width?: string;
}>`
  ${props => {
    return props.isRowHeader
      ? {}
      : {
          background: props.isInverse
            ? props.theme.colors.neutral1000
            : props.theme.colors.neutral150,
          borderBottom: '2px solid',
          fontWeight: 'bold',
          verticalAlign: 'bottom',
        };
  }}

  ${baseTableCellStyle}

  font-size: ${props =>
    props.isRowHeader ? 'inherit' : props.theme.typeScale.size02.fontSize};
  max-width: ${props => (props.isRowHeader ? 'none' : props.width || '0')};
  overflow: ${props => (props.isRowHeader ? 'visible' : 'hidden')};
  text-overflow: ${props => (props.isRowHeader ? 'clip' : 'ellipsis')};
  white-space: ${props => (props.isRowHeader ? 'normal' : 'nowrap')};

  &:first-child {
    border-radius: ${props =>
      props.hasSquareCorners ? '0' : `${props.theme.borderRadius} 0 0 0`};
  }
  &:last-child {
    border-radius: ${props =>
      props.hasSquareCorners ? '0' : `0 ${props.theme.borderRadius} 0 0`};
  }

  ${props =>
    props.isSortable &&
    css`
      padding: 0;
    `}

  ${props =>
    props.width &&
    css`
      width: ${props.width};
    `}
`;

const SortButton = styled.button<{
  density?: TableDensity;
  isInverse?: boolean;
  textAlign?: TableCellAlign;
}>`
  align-items: flex-end;
  background: none;
  border: 0;
  color: ${props =>
    props.isInverse
      ? props.theme.colors.neutral0
      : props.theme.colors.brand.navy};
  display: flex;
  font-size: ${props => props.theme.typeScale.size02.fontSize};
  justify-content: ${props =>
    props.textAlign === TableCellAlign.right ? 'flex-end' : 'flex-start'};
  margin: 0;
  min-width: 0;
  overflow: hidden;
  padding: ${props => buildCellPaddingStyle(props.density, props.theme)};
  text-align: left;
  width: 100%;

  &:hover,
  &:focus {
    cursor: pointer;
    background: ${props =>
      props.isInverse
        ? transparentize(0.85, props.theme.colors.neutral0)
        : transparentize(0.93, props.theme.colors.neutral900)};

    svg {
      fill: ${props =>
        props.isInverse
          ? props.theme.colors.neutral0
          : props.theme.colors.neutral700};
    }
  }

  &:focus {
    outline: 2px solid
      ${props =>
        props.isInverse
          ? props.theme.colors.focusInverse
          : props.theme.colors.focus};
    outline-offset: -2px;
  }
`;

const HeaderText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconWrapper = styled.span`
  flex-shrink: 0;
  padding-left: ${props => props.theme.spaceScale.spacing03};
  position: relative;
  top: ${props => props.theme.spaceScale.spacing02};
`;

export const TableHeaderCell = React.forwardRef<
  HTMLTableCellElement,
  TableHeaderCellProps
>((props, ref) => {
  const {
    align,
    children,
    isRowHeader = false,
    isSortable,
    onSort,
    scope = isRowHeader ? TableHeaderCellScope.row : TableHeaderCellScope.col,
    sortDirection,
    testId,
    width,
    header,
    ...other
  } = props;
  const theme = React.useContext(ThemeContext);
  const tableContext = React.useContext(TableContext);
  const i18n = React.useContext(I18nContext);

  function handleSort() {
    onSort && typeof onSort === 'function' && onSort();
  }

  const SortIcon = getTableSortIcon({
    sortDirection: sortDirection,
    isInverse: tableContext.isInverse,
    theme,
  });

  const widthString = typeof width === 'number' ? `${width}px` : width;

  const sortDirectionLabel =
    getAriaSortLabel(sortDirection) || i18n.table.selectable.sortDirectionNone;
  const sortRowsAriaLabel = isSortable
    ? i18n.table.selectable.sortButtonAriaLabel.replace('{labelText}', header) +
      `, ${sortDirectionLabel}`
    : undefined;

  return (
    <StyledTableHeaderCell
      {...other}
      aria-sort={getAriaSort(sortDirection)}
      data-testid={testId}
      density={tableContext.density}
      hasSquareCorners={tableContext.hasSquareCorners}
      hasVerticalBorders={tableContext.hasVerticalBorders}
      isInverse={tableContext.isInverse}
      ref={ref}
      isRowHeader={isRowHeader}
      isSortable={isSortable}
      scope={scope}
      textAlign={align || TableCellAlign.left}
      theme={theme}
      width={widthString}
    >
      {isSortable ? (
        <SortButton
          aria-label={sortRowsAriaLabel}
          density={tableContext.density}
          isInverse={tableContext.isInverse}
          onClick={handleSort}
          textAlign={align || TableCellAlign.left}
          theme={theme}
        >
          <HeaderText>{children}</HeaderText>
          <IconWrapper theme={theme}>{SortIcon}</IconWrapper>
        </SortButton>
      ) : (
        <>{children}</>
      )}
    </StyledTableHeaderCell>
  );
});

TableHeaderCell.displayName = 'TableHeaderCell';
